import React from "react";

import { List } from "@/components/organisms/List";
import { useSelectContext, type SelectSize } from "./Select.hooks";

// Map Select sizes to List sizes (List only supports sm/md/lg)
const LIST_SIZE: Record<SelectSize, "sm" | "md" | "lg"> = {
  sm: "sm",
  md: "md",
  lg: "lg",
};

/**
 * The floating options panel rendered inside the Popper.
 *
 * Accepts a render-function child identical to `List.Items`:
 * ```tsx
 * <Select.Options>
 *   {(item) => <Select.Option value={item}>{item.label}</Select.Option>}
 * </Select.Options>
 * ```
 */
export function SelectOptions<T>({
  children,
  filterOptions,
  searchPlaceholder,
}: {
  /**
   * Render function called for each option in `data`.
   * Return a `<Select.Option>` (or `<Select.SearchInput>` as a sibling).
   */
  children: (item: T, index: number, isActive?: boolean) => React.ReactNode;
  /** Optional client-side filter function — enables the search input when provided. */
  filterOptions?: (options: T[], filter: string) => T[];
  searchPlaceholder?: string;
}) {
  const { data, getOptionKey, onSelect, size } = useSelectContext<T>();

  return (
    <List<T>
      data={data}
      getRowKey={getOptionKey}
      onItemClick={onSelect}
      filterOptions={filterOptions}
      size={LIST_SIZE[size]}
      autofocus
      overscanCount={data.length}
    >
      {filterOptions && (
        <List.SearchInput placeholder={searchPlaceholder ?? "Search…"} />
      )}
      <List.Items<T>>{children}</List.Items>
    </List>
  );
}
